import React, { useMemo, useState, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L, { Marker as LeafletMarker } from 'leaflet';
import 'leaflet/dist/leaflet.css';

import {
  SienaguardService,
  SienaguardServiceDetail,
  IPInfo,
} from '../../../src/api/sienaguardService';

interface IPGlobalMapProps {
  services: SienaguardService[];
  serviceDetailsMap: Record<string, SienaguardServiceDetail>;
}

const defaultIcon = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
  shadowAnchor: [12, 41],
});

const IPGlobalMap: React.FC<IPGlobalMapProps> = ({ services, serviceDetailsMap }) => {
  const [selectedDomain, setSelectedDomain] = useState<string>('all');

  // 1. Recolectar TODAS las IPs de TODOS los servicios
  const allIPs = useMemo<IPInfo[]>(() => {
    const result: IPInfo[] = [];
    for (const svc of services) {
      const detail = serviceDetailsMap[svc.id];
      if (detail?.ip_info) {
        result.push(...detail.ip_info);
      }
    }
    return result;
  }, [services, serviceDetailsMap]);

  // Filtramos según el dominio seleccionado
  const filteredIPs = useMemo(() => {
    if (selectedDomain === 'all') return allIPs;
    const foundSvc = services.find(
      (s) => s.domain.toLowerCase() === selectedDomain.toLowerCase()
    );
    if (!foundSvc) return [];
    return serviceDetailsMap[foundSvc.id]?.ip_info || [];
  }, [selectedDomain, allIPs, services, serviceDetailsMap]);

  // Solo las IPs con coords válidas para marcadores
  const validIPs = filteredIPs.filter(
    (ip) =>
      ip.latitude != null &&
      ip.longitude != null &&
      !Number.isNaN(ip.latitude) &&
      !Number.isNaN(ip.longitude)
  );

  // Centro del mapa
  const defaultCenter: [number, number] = validIPs.length
    ? [validIPs[0].latitude!, validIPs[0].longitude!]
    : [0, 0];
  const zoomLevel = validIPs.length ? 2 : 1;

  // Array de dominios (selector)
  const domainOptions = useMemo(
    () => ['all', ...services.map((svc) => svc.domain)],
    [services]
  );

  // -- REFERENCIAS para Marker y para contenedor del mapa --
  const markerRefs = useRef<(LeafletMarker | null)[]>([]);

  // Para scrollear el mapa a la vista
  const mapContainerRef = useRef<HTMLDivElement>(null);

  // Cuando clican una tarjeta, abrimos su popup
  const handleCardClick = useCallback(
    (ip: IPInfo) => {
      // 1) Buscamos el índice de esa IP en validIPs
      const idx = validIPs.findIndex(
        (item) => item.ip === ip.ip && item.domain === ip.domain
      );
      if (idx === -1) return;

      // 2) Scrollear hasta el mapa
      mapContainerRef.current?.scrollIntoView({ behavior: 'smooth' });

      // 3) Abrir el popup de ese marcador
      //    Esperar un pequeño delay para que el mapa ya esté visible
      setTimeout(() => {
        markerRefs.current[idx]?.openPopup();
      }, 600);
    },
    [validIPs]
  );

  return (
    <div className="bg-gradient-to-b from-[#0A1723] to-[#050C12] text-white p-6 rounded-xl shadow mt-4">
      <h3 className="text-xl font-bold mb-4 text-center">Mapa Global de IPs</h3>

      {/* Selector de dominio */}
      <div className="mb-4 flex justify-center">
        <label className="mr-2 font-semibold self-center">Dominio:</label>
        <select
          value={selectedDomain}
          onChange={(e) => setSelectedDomain(e.target.value)}
          className="bg-[#00FF69] text-black font-semibold px-3 py-1 rounded"
        >
          {domainOptions.map((dom) => (
            <option key={dom} value={dom}>
              {dom === 'all' ? 'Todos los servicios' : dom}
            </option>
          ))}
        </select>
      </div>

      {/* Contenedor del mapa (más alto). Le ponemos ref para scrollear */}
      <div className="w-full relative mb-6" style={{ height: '600px' }} ref={mapContainerRef}>
        <MapContainer
          center={defaultCenter}
          zoom={zoomLevel}
          minZoom={2}
          maxBounds={[[-90, -180], [90, 180]]}
          maxBoundsViscosity={1.0}
          style={{ width: '100%', height: '100%' }}
          scrollWheelZoom
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/">
              OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {validIPs.map((ip, idx) => (
            <Marker
              key={idx}
              position={[ip.latitude!, ip.longitude!]}
              icon={defaultIcon}
              // Guardar referencia en markerRefs
              ref={(ref) => {
                markerRefs.current[idx] = ref;
              }}
            >
              <Popup>
                {/* Puedes estilizar con clases personalizadas */}
                <div className="text-center min-w-[120px]">
                  <p className="font-bold">{ip.domain || ip.ip}</p>
                  {ip.city && <p>{ip.city}</p>}
                  {ip.country && <p>{ip.country}</p>}
                  {ip.isp && <p className="text-xs">ISP: {ip.isp}</p>}
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>

      {/* Tarjetas IP (3 columnas en md+) */}
      <h4 className="text-lg font-semibold mb-2">IP Details</h4>
      {filteredIPs.length === 0 ? (
        <p className="text-sm text-gray-300">
          No hay información de IP disponible para este servicio.
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {filteredIPs.map((ip, idx) => (
            <div
              key={idx}
              onClick={() => handleCardClick(ip)}
              className="bg-[#162536] rounded p-4 text-sm flex flex-col gap-1 
                         transition-colors hover:bg-[#1c2e3d] cursor-pointer"
            >
              <span className="font-bold">{ip.domain || ip.ip}</span>
              {ip.country && <span>País: {ip.country}</span>}
              {ip.city && <span>Ciudad: {ip.city}</span>}
              {ip.isp && <span>ISP: {ip.isp}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default IPGlobalMap;
